import { Icon } from "@/assets/icons";
import { cn } from "@/utils";
import React, { ReactNode, useCallback, useRef, useState } from "react";
import { ButtonIcon } from "./ButtonIcon";
import { Checkbox } from "./Checkbox";
import { LoadingSpinner } from "./Loading";
import Popover from "./Popover";

export interface ColumnType<T = any> {
  title: React.ReactNode;
  dataIndex?: keyof T;
  key?: string;
  width?: number | string;
  align?: "left" | "center" | "right";
  render?: (value: any, record: T, index: number) => React.ReactNode;
  className?: string;
  enableSorting?: boolean;
  isTruncated?: boolean;
  displayTitle?: boolean;
}

export type SortingState<T> = {
  order: "ASC" | "DESC";
  orderBy: keyof T;
};

export interface RowSelection<T = any> {
  selectedRowKeys?: string[];
  onChange?: (selectedRowKeys: string[], selectedRows: T[]) => void;
  onSelect?: (
    record: T,
    selected: boolean,
    selectedRows: T[],
    nativeEvent: Event
  ) => void;
  onSelectAll?: (selected: boolean, selectedRows: T[], changeRows: T[]) => void;
  getCheckboxProps?: (record: T) => { disabled?: boolean; name?: string };
  hideSelectAll?: boolean;
  fixed?: boolean;
  columnWidth?: string | number;
  columnTitle?: string | React.ReactNode;
}

export interface TableProps<T = any> {
  ref?: React.Ref<HTMLTableElement> | undefined;
  columns: ColumnType<T>[];
  dataSource: T[];
  rowKey?: keyof T | ((record: T) => string);
  className?: string;
  size?: "small" | "middle" | "large";
  bordered?: boolean;
  loading?: boolean;
  loadingClassname?: string;
  isFetchMore?: boolean;
  pagination?: boolean;
  scroll?: {
    x?: number | string;
    y?: number | string;
  };
  onRow?: (
    record: T,
    index: number
  ) => React.HTMLAttributes<HTMLTableRowElement>;
  rowSelection?: RowSelection<T>;
  showAction?: boolean;
  renderAction?: (
    record: T
  ) => { title: ReactNode | string; action?: () => void }[];
  onSortingChange?: (sorting: SortingState<T>) => void;
  onScrollToBottom?: () => void;
  shouldHighlightRow?: (record: T) => boolean;
}

export function Table<T extends Record<string, any>>(props: TableProps<T>) {
  const {
    ref,
    columns,
    dataSource,
    rowKey = "id",
    className = "",
    size = "middle",
    bordered = false,
    loading = false,
    isFetchMore = false,
    scroll,
    onRow,
    rowSelection,
    showAction = false,
    renderAction,
    onSortingChange,
    onScrollToBottom,
    loadingClassname = "py-8",
  } = props;

  const [tableData, setTableData] = useState<SortingState<T>>(
    {} as SortingState<T>
  );

  const tableContainerRef = useRef(null);
  const isAtBottomRef = useRef(false);

  const getRowKey = (record: T, index: number): string => {
    if (typeof rowKey === "function") {
      return rowKey(record);
    }
    return record[rowKey]?.toString() || index.toString();
  };

  const getPaddingClass = () => {
    switch (size) {
      case "small":
        return "px-2 py-0.5";
      case "large":
        return "px-4 py-1";
      default:
        return "px-3 py-1";
    }
  };

  // Get title styling - responsive theo size
  const getTitleClass = () => {
    const baseClass = "font-bold text-auro_metal_saurus h-10 text-left";
    switch (size) {
      case "small":
        return `text-xs leading-4 ${baseClass}`; // 12px, 16px leading
      case "large":
        return `text-sm leading-5 ${baseClass}`; // 14px, 20px leading
      default:
        return `text-xs leading-[18px] ${baseClass}`; // 12px, 18px leading (default)
    }
  };

  // Get cell text styling - default: 14px, leading-22px, font-400
  const getCellTextClass = () => {
    switch (size) {
      case "small":
        return "text-xs leading-5 font-normal"; // 12px, 20px leading
      case "large":
        return "text-base leading-6 font-normal"; // 16px, 24px leading
      default:
        return "text-sm leading-[22px] font-normal"; // 14px, 22px leading
    }
  };

  const getScrollStyle = (): React.CSSProperties => {
    if (!scroll) return {};
    const style: React.CSSProperties = {};
    if (scroll.x) {
      style.maxWidth = scroll.x;
    }
    if (scroll.y) {
      style.maxHeight = scroll.y;
    }
    return style;
  };

  // Row selection handlers
  const handleSelectRow = (
    record: T,
    selected: boolean,
    nativeEvent: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!rowSelection?.onChange) return;

    const recordKey = getRowKey(record, dataSource.indexOf(record));
    const currentKeys = rowSelection.selectedRowKeys || [];
    let newSelectedKeys: string[];
    let newSelectedRows: T[];

    if (selected) {
      newSelectedKeys = [...currentKeys, recordKey];
      newSelectedRows = dataSource.filter((item, index) =>
        newSelectedKeys.includes(getRowKey(item, index))
      );
    } else {
      newSelectedKeys = currentKeys.filter((key) => key !== recordKey);
      newSelectedRows = dataSource.filter((item, index) =>
        newSelectedKeys.includes(getRowKey(item, index))
      );
    }

    rowSelection.onChange(newSelectedKeys, newSelectedRows);
    rowSelection.onSelect?.(
      record,
      selected,
      newSelectedRows,
      nativeEvent.nativeEvent
    );
  };

  const handleSelectAll = (selected: boolean) => {
    if (!rowSelection?.onChange) return;

    const currentKeys = rowSelection.selectedRowKeys || [];

    let newSelectedKeys: string[];
    let newSelectedRows: T[];
    let changeRows: T[];

    if (selected) {
      const selectableRows = dataSource.filter((record) => {
        const checkboxProps = rowSelection.getCheckboxProps?.(record);
        return !checkboxProps?.disabled;
      });
      const selectableKeys = selectableRows.map((record) => {
        const originalIndex = dataSource.indexOf(record);
        return getRowKey(record, originalIndex);
      });
      newSelectedKeys = [...new Set([...currentKeys, ...selectableKeys])];
      newSelectedRows = dataSource.filter((item, index) =>
        newSelectedKeys.includes(getRowKey(item, index))
      );
      changeRows = selectableRows;
    } else {
      newSelectedKeys = [];
      newSelectedRows = [];
      changeRows = dataSource.filter((item, index) =>
        currentKeys.includes(getRowKey(item, index))
      );
    }

    rowSelection.onChange(newSelectedKeys, newSelectedRows);
    rowSelection.onSelectAll?.(selected, newSelectedRows, changeRows);
  };

  const isRowSelected = (record: T, index: number): boolean => {
    if (!rowSelection?.selectedRowKeys) return false;
    const recordKey = getRowKey(record, index);
    return rowSelection.selectedRowKeys.includes(recordKey);
  };

  const isAllSelected = (): boolean => {
    if (!rowSelection?.selectedRowKeys || dataSource.length === 0) return false;
    const selectableRows = dataSource.filter((record) => {
      const checkboxProps = rowSelection.getCheckboxProps?.(record);
      return !checkboxProps?.disabled;
    });
    return (
      selectableRows.length > 0 &&
      selectableRows.every((record, index) => isRowSelected(record, index))
    );
  };

  const isIndeterminate = (): boolean => {
    if (!rowSelection?.selectedRowKeys || dataSource.length === 0) return false;
    const selectableRows = dataSource.filter((record) => {
      const checkboxProps = rowSelection.getCheckboxProps?.(record);
      return !checkboxProps?.disabled;
    });
    const selectedCount = selectableRows.filter((record, index) =>
      isRowSelected(record, index)
    ).length;
    return selectedCount > 0 && selectedCount < selectableRows.length;
  };

  // Build columns with selection column
  const finalColumns = React.useMemo(() => {
    const _columns = [...columns];
    const actionColumn: ColumnType<T> = {
      title: " ",
      dataIndex: "_action",
      key: "_action",
      width: 56,
      align: "center",
      className: "",
    };

    if (showAction) _columns.push(actionColumn);

    if (!rowSelection) return _columns;

    const SelectAllCheckbox = () => {
      const checkboxRef = React.useRef<HTMLInputElement>(null);

      React.useEffect(() => {
        if (checkboxRef.current) {
          checkboxRef.current.indeterminate = isIndeterminate();
        }
      }, []);

      return (
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={isAllSelected()}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="h-4 w-4 accent-cyan rounded-full cursor-pointer"
        />
      );
    };

    const selectionColumn: ColumnType<T> = {
      title:
        rowSelection.columnTitle ||
        (!rowSelection.hideSelectAll ? <SelectAllCheckbox /> : ""),
      dataIndex: "__selection__" as keyof T,
      key: "__selection__",
      width: rowSelection.columnWidth || 60,
      align: "center",
      render: (_, record, index) => {
        const checkboxProps = rowSelection.getCheckboxProps?.(record) || {};
        const selected = isRowSelected(record, index);

        return (
          <Checkbox
            name={checkboxProps.name}
            checked={selected}
            disabled={checkboxProps.disabled}
            onChange={(e) => handleSelectRow(record, e.target.checked, e)}
            className="disabled:opacity-50"
          />
        );
      },
    };

    return [selectionColumn, ..._columns];
  }, [columns, rowSelection, dataSource]);

  const checkIfAtBottom = useCallback(() => {
    if (!tableContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5; // Ngưỡng 5px

    if (isAtBottom && !isAtBottomRef.current) {
      isAtBottomRef.current = true;
      onScrollToBottom?.();
    } else if (!isAtBottom) {
      isAtBottomRef.current = false;
    }
  }, [onScrollToBottom]);

  // Handle sorting
  const handleToggleSorting = (column: ColumnType<T>) => {
    if (!column.enableSorting || !column.dataIndex) return;

    // Update new order
    let newOrder = null;
    const order = tableData.order;
    const orderBy = tableData.orderBy;

    if (orderBy === column.dataIndex) {
      if (order === "ASC") {
        newOrder = "DESC";
      } else if (order === "DESC") {
        newOrder = null; // Reset sorting
      } else {
        newOrder = "ASC";
      }
    } else {
      newOrder = "ASC";
    }

    if (newOrder === null) {
      const newSorting = {} as SortingState<T>;
      setTableData(newSorting);
      onSortingChange?.({ ...newSorting });
    } else {
      const newSorting = {
        order: newOrder,
        orderBy: column.dataIndex,
      } as SortingState<T>;
      setTableData(newSorting);
      onSortingChange?.({ ...newSorting });
    }
  };

  return (
    <div
      className={cn(`
        overflow-y-scroll visible-scrollbar
        ${bordered ? "border border-gray-200 rounded-b-lg" : ""}
        ${className} 
      `)}
      style={getScrollStyle()}
      ref={tableContainerRef}
      onScroll={checkIfAtBottom}
    >
      <table ref={ref} className={`w-full table-fixed bg-white`}>
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="bg-gray-50 border-b border-gray-200">
            {finalColumns.map((column, index) => (
              <th
                key={column.key || column.dataIndex?.toString() || index}
                className={cn(`
                    ${getPaddingClass()}
                    ${getTitleClass()}
                    ${column.align === "center" ? "text-center" : ""}
                    ${column.align === "right" ? "text-right" : ""}
                    ${
                      bordered ? "border-r border-gray-200 last:border-r-0" : ""
                    }
                    ${column.className || ""}
                    ${
                      column.enableSorting
                        ? "cursor-pointer select-none"
                        : "cursor-default"
                    }
                  `)}
                style={{ width: column.width }}
                onClick={() => handleToggleSorting(column)}
                role={column.enableSorting ? "button" : undefined}
                aria-label={
                  column.enableSorting ? `Sort by ${column.title}` : undefined
                }
              >
                {column.title}

                {column.enableSorting &&
                  column.dataIndex &&
                  tableData.orderBy === column.dataIndex && (
                    <span className="float-end text-gray-400">
                      {tableData.order === "ASC" && "↑"}
                      {tableData.order === "DESC" && "↓"}
                    </span>
                  )}

                {column.enableSorting &&
                  tableData.order !== "ASC" &&
                  tableData.order !== "DESC" && (
                    <span className="float-end text-gray-400">
                      <Icon name="swap_vert" />
                    </span>
                  )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!loading ? (
            dataSource.length === 0 ? (
              <tr>
                <td
                  colSpan={finalColumns.length}
                  className={cn("text-center text-gray-500", loadingClassname)}
                >
                  データがありません。
                </td>
              </tr>
            ) : (
              dataSource.map((record, rowIndex) => {
                const rowProps = onRow ? onRow(record, rowIndex) : {};
                const selected = isRowSelected(record, rowIndex);

                // Enhanced row click handler that respects interactive elements
                const handleRowClick = (
                  e: React.MouseEvent<HTMLTableRowElement>
                ) => {
                  // Check if the clicked element or any of its parents is an interactive element
                  const target = e.target as HTMLElement;

                  // More comprehensive but targeted list of interactive elements
                  const isInteractiveElement = target.closest(
                    [
                      // Form controls
                      "button",
                      "input",
                      "select",
                      "textarea",
                      "a",
                      // ARIA roles
                      '[role="button"]',
                      '[role="checkbox"]',
                      '[role="radio"]',
                      '[role="combobox"]',
                      '[role="listbox"]',
                      '[role="option"]',
                      '[role="menuitem"]',
                      // Common interactive classes
                      ".clickable",
                      ".interactive",
                      "[data-clickable]",
                      // React Select specific (but only the control part, not the portal)
                      ".basic-multi-select .select__control",
                      ".basic-multi-select .select__dropdown-indicator",
                      ".basic-multi-select .select__clear-indicator",
                      ".basic-multi-select .select__multi-value__remove",
                      // Date picker triggers
                      '[data-testid*="calendar"]',
                      '[data-testid*="date"]',
                      ".react-datepicker__input-container",
                      ".react-datepicker__close-icon",
                      // Any element with onclick handler
                      "[onclick]",
                    ].join(", ")
                  );

                  // If clicked on interactive element, don't trigger row click
                  if (isInteractiveElement) {
                    return;
                  }

                  // Check if the event target is actually within this row
                  const isFromCurrentRow = e.currentTarget.contains(target);
                  if (!isFromCurrentRow) {
                    return;
                  }

                  // Otherwise, trigger the original onClick if it exists
                  if (rowProps.onClick) {
                    rowProps.onClick(e);
                  }
                };

                return (
                  <tr
                    key={getRowKey(record, rowIndex)}
                    className={cn(`
                      border-b border-gray-100 last:border-b-0
                      hover:!bg-grey-300 transition-colors duration-150
                      cursor-pointer
                      h-10
                      ${selected ? "bg-blue-50" : ""}
                      ${rowProps.className || ""}
                    `)}
                    {...rowProps}
                    onClick={handleRowClick}
                  >
                    {finalColumns.map((column, colIndex) => {
                      const value =
                        column.dataIndex === "__selection__"
                          ? null
                          : column.dataIndex
                          ? record[column.dataIndex]
                          : null;
                      const displayValue = column.render
                        ? column.render(value, record, rowIndex)
                        : value;

                      // Handle cell click with proper event management
                      const handleCellClick = (
                        e: React.MouseEvent<HTMLTableCellElement>
                      ) => {
                        const target = e.target as HTMLElement;

                        // Check if this cell contains elements that should handle their own clicks
                        const hasInteractiveContent = target.closest(
                          [
                            "button",
                            "a",
                            "input",
                            "select",
                            "textarea",
                            '[role="button"]',
                            '[role="combobox"]',
                            '[role="checkbox"]',
                            "[data-modal-trigger]",
                            "[data-clickable]",
                            ".clickable",
                            ".interactive",
                            "[onclick]",
                            // React Select elements
                            ".basic-multi-select",
                            '[class*="select__"]',
                            // Date picker elements
                            ".react-datepicker__input-container",
                            '[data-testid*="calendar"]',
                            '[data-testid*="date"]',
                          ].join(", ")
                        );

                        // If clicked on interactive content, stop row click
                        if (hasInteractiveContent) {
                          e.stopPropagation();
                        }
                      };

                      return (
                        <td
                          key={
                            column.key ||
                            column.dataIndex?.toString() ||
                            colIndex
                          }
                          className={cn(`
                            ${getPaddingClass()}
                            ${getCellTextClass()}
                            h-10
                            ${column.align === "center" ? "text-center" : ""}
                            ${column.align === "right" ? "text-right" : ""}
                            ${
                              bordered
                                ? "border-r border-gray-200 last:border-r-0"
                                : ""
                            }
                            ${column.className || ""}
                            ${
                              column.key === "_action" &&
                              "!p-0 !overflow-visible"
                            }
                            ${column.isTruncated ? "truncate" : ""}
                          `)}
                          style={{
                            width: column.width,
                            maxWidth: column.width,
                          }}
                          onClick={
                            column.key === "_action"
                              ? undefined
                              : handleCellClick
                          }
                          title={
                            column.displayTitle &&
                            typeof displayValue === "string"
                              ? displayValue
                              : undefined
                          }
                        >
                          {displayValue}
                          {column.key === "_action" && (
                            <Action
                              render={
                                renderAction
                                  ? () => renderAction(record)
                                  : () => []
                              }
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )
          ) : (
            <tr>
              <td colSpan={finalColumns.length} className={loadingClassname}>
                <div className="flex justify-center items-center">
                  <LoadingSpinner />
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {isFetchMore && (
        <p className="text-center text-gray-500 text-xs animate-bounce">
          Loading...
        </p>
      )}
    </div>
  );
}

function Action({
  render,
}: {
  render: () => {
    title: ReactNode;
    action?: () => void;
    enableStopPropagation?: boolean;
    actionClassName?: string;
  }[];
}) {
  return (
    <Popover
      side="bottom"
      align="end"
      trigger={(open) => <ButtonIcon name="more" forceActive={open} />}
      triggerClassName="flex items-center justify-center mx-auto"
      alignOffset={12}
      contentClassName="z-30"
    >
      <div className="border rounded-lg bg-white shadow-shadow_light_card *:border-b last:*:border-none">
        {render().map((item, index) => (
          <div
            key={index}
            className={cn(
              "p-2 px-4 hover:bg-gray-100 text-nowrap cursor-pointer",
              item.actionClassName
            )}
            onClick={(e) => {
              if (item.enableStopPropagation) {
                e.stopPropagation();
                e.preventDefault();
              }
              item.action?.();
            }}
          >
            <div className="w-full text-left">{item.title}</div>
          </div>
        ))}
      </div>
    </Popover>
  );
}
